import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/common/Section';

const PageContainer = styled(motion.div)`
  padding-top: 100px;
  overflow: hidden;
`;

const PrivacyContainer = styled(motion.div)`
  max-width: 900px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.md};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: ${({ theme }) => theme.colors.gradient.accent};
    border-top-left-radius: ${({ theme }) => theme.borderRadius.lg};
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.lg};
  }
`;

const Title = styled(motion.h1)`
  margin-bottom: ${({ theme }) => theme.space.xl};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.primary};
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const SectionTitle = styled(motion.h2)`
  margin: ${({ theme }) => theme.space.xl} 0 ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.primary};
  font-family: ${({ theme }) => theme.fonts.heading};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  position: relative;
  display: inline-block;
  padding-left: ${({ theme }) => theme.space.sm};
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

const Paragraph = styled(motion.p)`
  margin-bottom: ${({ theme }) => theme.space.md};
  line-height: 1.8;
  font-family: ${({ theme }) => theme.fonts.body};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 1.05rem;
`;

const List = styled(motion.ul)`
  margin-bottom: ${({ theme }) => theme.space.md};
  padding-left: ${({ theme }) => theme.space.lg};
  
  li {
    margin-bottom: ${({ theme }) => theme.space.sm};
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.grey[800]};
    font-family: ${({ theme }) => theme.fonts.body};
    position: relative;
    
    &::marker {
      color: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const LastUpdated = styled(motion.p)`
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[600]};
  margin-top: ${({ theme }) => theme.space.xl};
  padding-top: ${({ theme }) => theme.space.md};
  border-top: 1px solid ${({ theme }) => theme.colors.grey[300]};
  font-size: 0.9rem;
  text-align: right;
`;

const PrivacyPolicy: React.FC = () => {
  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Section backgroundColor="#F8F9FE">
        <PrivacyContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.1 }}
        >
          <Title
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.2 }}
          >
            Privacy Policy
          </Title>
          
          <Paragraph
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 50, damping: 15, delay: 0.3 }}
          >
            At Data Digital, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
          </Paragraph>
          
          <SectionTitle>Information We Collect</SectionTitle>
          <Paragraph>
            We may collect personal information that you voluntarily provide to us when you:
          </Paragraph>
          <List>
            <li>Register for our services or sign up for our newsletter</li>
            <li>Fill out a form or send us feedback</li>
            <li>Participate in surveys or contests</li>
            <li>Contact us via email, phone, or any other communication channel</li>
          </List>
          
          <SectionTitle>How We Use Your Information</SectionTitle>
          <Paragraph>
            The information we collect may be used in the following ways:
          </Paragraph>
          <List>
            <li>To personalize your experience and deliver the type of content and product offerings that interest you most</li>
            <li>To improve our website and services based on the feedback and information we receive from you</li>
            <li>To send periodic emails regarding your order or other products and services</li>
            <li>To follow up with you after correspondence (email or phone inquiries)</li>
          </List>
          
          <SectionTitle>Cookies and Tracking Technologies</SectionTitle>
          <Paragraph>
            We may use cookies, web beacons, tracking pixels, and other tracking technologies to help customize our website and improve your experience. When you access our website, your personal information is not collected through the use of tracking technology. Most browsers are set to accept cookies by default. You can remove or reject cookies, but be aware that such action could affect the availability and functionality of our website.
          </Paragraph>
          
          <SectionTitle>Third-Party Disclosure</SectionTitle>
          <Paragraph>
            We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties unless we provide you with advance notice. This does not include website hosting partners and other parties who assist us in operating our website, conducting our business, or servicing you, so long as those parties agree to keep this information confidential.
          </Paragraph>
          
          <SectionTitle>Security Measures</SectionTitle>
          <Paragraph>
            We implement a variety of security measures to maintain the safety of your personal information when you enter, submit, or access your personal information. We use secure networks, encryption, and regular malware scanning to protect your data.
          </Paragraph>
          
          <SectionTitle>Your Rights</SectionTitle>
          <Paragraph>
            You have the right to access, correct, or delete your personal information at any time. You can also object to our processing of your personal data or request that we restrict the processing of your information. To exercise these rights, please contact us using the information provided below.
          </Paragraph>
          
          <SectionTitle>Changes to This Privacy Policy</SectionTitle>
          <Paragraph>
            We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </Paragraph>
          
          <SectionTitle>Contact Us</SectionTitle>
          <Paragraph>
            If you have any questions about this Privacy Policy, please contact us at:
          </Paragraph>
          <Paragraph>
            Email: connect@datadigital.in<br />
            Contact: 6203306234<br />
            Address: Saguna More, Patna, Bihar
          </Paragraph>
          
          <LastUpdated>Last Updated: May 25, 2025</LastUpdated>
        </PrivacyContainer>
      </Section>
    </PageContainer>
  );
};

export default PrivacyPolicy;
